import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import {
  Car, Users, DollarSign, TrendingUp, Menu, X, LogOut, User,
  BarChart3, Settings, Shield, MapPin, Star, Check, Ban
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [rides, setRides] = useState([]);
  const [userFilter, setUserFilter] = useState('all');
  const [rideFilter, setRideFilter] = useState('all');

  useEffect(() => {
    fetchStats();
    fetchUsers();
    fetchRides();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      }
    }
  };

  const fetchUsers = async () => {
    try {
      const url = userFilter === 'all' 
        ? `${API}/admin/users`
        : `${API}/admin/users?role=${userFilter}`;
      const response = await axios.get(url, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchRides = async () => {
    try {
      const url = rideFilter === 'all'
        ? `${API}/admin/rides`
        : `${API}/admin/rides?status=${rideFilter}`;
      const response = await axios.get(url, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setRides(response.data);
    } catch (error) {
      console.error('Failed to fetch rides:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [userFilter]);

  useEffect(() => {
    fetchRides();
  }, [rideFilter]);

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, 
        { is_active: !currentStatus },
        {
          headers: getAuthHeaders(),
          withCredentials: true
        }
      );
      toast.success(`User ${currentStatus ? 'deactivated' : 'activated'}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleChangeUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`,
        { role: newRole },
        {
          headers: getAuthHeaders(),
          withCredentials: true
        }
      );
      toast.success(`User role updated to ${newRole}`);
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-500/20 text-amber-400',
      accepted: 'bg-blue-500/20 text-blue-400',
      in_progress: 'bg-cyan-500/20 text-cyan-400',
      completed: 'bg-emerald-500/20 text-emerald-400',
      cancelled: 'bg-red-500/20 text-red-400'
    };
    return colors[status] || 'bg-zinc-500/20 text-zinc-400';
  };

  const getRoleBadge = (role) => {
    const badges = {
      rider: 'bg-blue-500/20 text-blue-400',
      driver: 'bg-emerald-500/20 text-emerald-400',
      admin: 'bg-purple-500/20 text-purple-400'
    };
    return badges[role] || 'bg-zinc-500/20 text-zinc-400';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      <Toaster position="top-right" theme="dark" />
      
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:block fixed left-0 top-0 h-full w-64 bg-zinc-900 border-r border-zinc-800 z-40">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-purple-500 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="text-xl font-bold text-white">Santorini Best Experience</span>
              <p className="text-xs text-purple-400">Admin Panel</p>
            </div>
          </div>
          
          <nav className="space-y-2">
            {[
              { id: 'overview', label: 'Overview', icon: <BarChart3 className="w-5 h-5" /> },
              { id: 'users', label: 'Users', icon: <Users className="w-5 h-5" /> },
              { id: 'rides', label: 'Rides', icon: <Car className="w-5 h-5" /> },
              { id: 'revenue', label: 'Revenue', icon: <DollarSign className="w-5 h-5" /> }
            ].map(item => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                  activeTab === item.id
                    ? 'bg-purple-500/10 text-purple-400'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
                data-testid={`admin-nav-${item.id}`}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </nav>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-zinc-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
              <User className="w-5 h-5 text-zinc-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-purple-400">Administrator</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="w-full border-zinc-700 text-zinc-400 hover:text-white"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-zinc-950/90 backdrop-blur-lg border-b border-zinc-800">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-white">Admin</span>
          </div>
          
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-zinc-400"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-zinc-950/95 pt-20 px-4">
          <nav className="space-y-2">
            {[
              { id: 'overview', label: 'Overview', icon: <BarChart3 className="w-5 h-5" /> },
              { id: 'users', label: 'Users', icon: <Users className="w-5 h-5" /> },
              { id: 'rides', label: 'Rides', icon: <Car className="w-5 h-5" /> },
              { id: 'revenue', label: 'Revenue', icon: <DollarSign className="w-5 h-5" /> }
            ].map(item => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveTab(item.id);
                  setMobileMenuOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-4 rounded-xl text-lg ${
                  activeTab === item.id
                    ? 'bg-purple-500/10 text-purple-400'
                    : 'text-zinc-400'
                }`}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </nav>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="w-full mt-6 border-zinc-700 text-zinc-400"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 px-4 lg:px-8 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="animate-fade-in">
            <h1 className="text-2xl font-bold text-white mb-8">Dashboard Overview</h1>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <Users className="w-8 h-8 text-blue-400 mb-4" />
                <p className="text-sm text-zinc-500">Total Users</p>
                <p className="text-3xl font-bold text-white">{stats.total_users}</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <Car className="w-8 h-8 text-cyan-400 mb-4" />
                <p className="text-sm text-zinc-500">Total Rides</p>
                <p className="text-3xl font-bold text-white">{stats.total_rides}</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <Check className="w-8 h-8 text-emerald-400 mb-4" />
                <p className="text-sm text-zinc-500">Completed</p>
                <p className="text-3xl font-bold text-white">{stats.completed_rides}</p>
              </div>
              <div className="bg-gradient-to-br from-purple-500/20 to-transparent border border-purple-500/30 rounded-xl p-6">
                <DollarSign className="w-8 h-8 text-purple-400 mb-4" />
                <p className="text-sm text-zinc-400">Platform Revenue</p>
                <p className="text-3xl font-bold text-white">${stats.total_revenue.toFixed(2)}</p>
                <p className="text-xs text-zinc-500 mt-1">10% commission</p>
              </div>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <h2 className="text-lg font-semibold text-white mb-4">User Breakdown</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-400" />
                      </div>
                      <span className="text-zinc-300">Riders</span>
                    </div>
                    <span className="text-xl font-bold text-white">{stats.total_riders}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <Car className="w-5 h-5 text-emerald-400" />
                      </div>
                      <span className="text-zinc-300">Drivers</span>
                    </div>
                    <span className="text-xl font-bold text-white">{stats.total_drivers}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                <h2 className="text-lg font-semibold text-white mb-4">Quick Stats</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400">Completion Rate</span>
                    <span className="text-white font-medium">
                      {stats.total_rides > 0 
                        ? ((stats.completed_rides / stats.total_rides) * 100).toFixed(1)
                        : 0}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400">Avg Revenue per Ride</span>
                    <span className="text-white font-medium">
                      ${stats.completed_rides > 0 
                        ? (stats.total_revenue / stats.completed_rides).toFixed(2)
                        : '0.00'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold text-white">Users Management</h1>
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger className="w-40 bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="all">All Users</SelectItem>
                  <SelectItem value="rider">Riders</SelectItem>
                  <SelectItem value="driver">Drivers</SelectItem>
                  <SelectItem value="admin">Admins</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left px-6 py-4 text-xs font-semibold text-zinc-400 uppercase">User</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-zinc-400 uppercase">Role</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-zinc-400 uppercase">Rating</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-zinc-400 uppercase">Rides</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-zinc-400 uppercase">Status</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-zinc-400 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.user_id} className="border-b border-zinc-800 hover:bg-zinc-800/30">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
                              {u.picture ? (
                                <img src={u.picture} alt="" className="w-full h-full rounded-full object-cover" />
                              ) : (
                                <User className="w-5 h-5 text-zinc-400" />
                              )}
                            </div>
                            <div>
                              <p className="text-white font-medium">{u.name}</p>
                              <p className="text-sm text-zinc-500">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${getRoleBadge(u.role)}`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                            <span className="text-white">{u.rating?.toFixed(1) || '5.0'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-zinc-300">{u.total_rides || 0}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                            u.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                          }`}>
                            {u.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Select 
                              value={u.role} 
                              onValueChange={(value) => handleChangeUserRole(u.user_id, value)}
                            >
                              <SelectTrigger className="w-24 h-8 bg-zinc-800 border-zinc-700 text-white text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-zinc-800 border-zinc-700">
                                <SelectItem value="rider">Rider</SelectItem>
                                <SelectItem value="driver">Driver</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleUserStatus(u.user_id, u.is_active)}
                              className={u.is_active ? 'text-red-400 hover:bg-red-500/10' : 'text-emerald-400 hover:bg-emerald-500/10'}
                            >
                              {u.is_active ? <Ban className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Rides Tab */}
        {activeTab === 'rides' && (
          <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <h1 className="text-2xl font-bold text-white">Rides Management</h1>
              <Select value={rideFilter} onValueChange={setRideFilter}>
                <SelectTrigger className="w-40 bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="all">All Rides</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="accepted">Accepted</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-4">
              {rides.map(ride => (
                <div key={ride.ride_id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-sm text-zinc-500">{formatDate(ride.created_at)}</p>
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium mt-1 ${getStatusColor(ride.status)}`}>
                        {ride.status}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-white">${ride.fare.toFixed(2)}</p>
                      <p className="text-xs text-purple-400">Platform: ${ride.platform_fee.toFixed(2)}</p>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-zinc-500 mb-1">RIDER</p>
                      <p className="text-white">{ride.rider_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500 mb-1">DRIVER</p>
                      <p className="text-white">{ride.driver_name || 'Not assigned'}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-2 h-2 rounded-full bg-emerald-500" />
                      <span className="text-zinc-400">{ride.pickup_location}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-2 h-2 rounded-full bg-cyan-500" />
                      <span className="text-zinc-400">{ride.destination}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 mt-4 pt-4 border-t border-zinc-800 text-sm text-zinc-500">
                    <span>{ride.distance_km.toFixed(1)} km</span>
                    {ride.rating && (
                      <span className="flex items-center gap-1">
                        <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                        {ride.rating}
                      </span>
                    )}
                    <span className={ride.payment_status === 'paid' ? 'text-emerald-400' : 'text-amber-400'}>
                      {ride.payment_status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Revenue Tab */}
        {activeTab === 'revenue' && stats && (
          <div className="animate-fade-in">
            <h1 className="text-2xl font-bold text-white mb-8">Revenue Analytics</h1>
            
            <div className="grid lg:grid-cols-3 gap-6 mb-8">
              <div className="bg-gradient-to-br from-purple-500/20 to-transparent border border-purple-500/30 rounded-2xl p-8">
                <DollarSign className="w-10 h-10 text-purple-400 mb-4" />
                <p className="text-zinc-400 mb-2">Total Platform Revenue</p>
                <p className="text-5xl font-bold text-white">${stats.total_revenue.toFixed(2)}</p>
                <p className="text-sm text-zinc-500 mt-2">10% of all completed rides</p>
              </div>
              
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8">
                <TrendingUp className="w-10 h-10 text-cyan-400 mb-4" />
                <p className="text-zinc-400 mb-2">Completed Rides</p>
                <p className="text-5xl font-bold text-white">{stats.completed_rides}</p>
                <p className="text-sm text-zinc-500 mt-2">out of {stats.total_rides} total</p>
              </div>
              
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8">
                <BarChart3 className="w-10 h-10 text-emerald-400 mb-4" />
                <p className="text-zinc-400 mb-2">Avg. Revenue per Ride</p>
                <p className="text-5xl font-bold text-white">
                  ${stats.completed_rides > 0 
                    ? (stats.total_revenue / stats.completed_rides).toFixed(2)
                    : '0.00'}
                </p>
                <p className="text-sm text-zinc-500 mt-2">platform commission</p>
              </div>
            </div>
            
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-6">Revenue Breakdown</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-zinc-800">
                  <span className="text-zinc-400">Total Ride Fares</span>
                  <span className="text-white font-semibold">${(stats.total_revenue * 10).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-zinc-800">
                  <span className="text-zinc-400">Driver Payouts (90%)</span>
                  <span className="text-emerald-400 font-semibold">${(stats.total_revenue * 9).toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-zinc-400">Platform Revenue (10%)</span>
                  <span className="text-purple-400 font-semibold">${stats.total_revenue.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
